import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, StatusBadge, Text, Title, UnstyledButton } from '@/shared/ui';
import type { StatusBadgeTone } from '@/shared/ui';
import {
  ChevronLeftIcon,
  Pencil1Icon,
  Share2Icon,
  StarFilledIcon,
  StarIcon,
} from '@radix-ui/react-icons';
import type { PropertyResponseType } from '@services';
import { normalizeTransactionTypeKeys } from '@/shared/utils/property-nested-write';
import { propertyAgencyCode } from '@/shared/utils/property-display';
import { getCategoryLabel } from '../PropertyFormDualPane/schema';
import { cn } from '@/shared/ui/utils/tailwind';

interface PropertyHeaderProps {
  property: PropertyResponseType;
  onBack: () => void;
  onEdit: () => void;
  onShare?: () => void;
  /** Opens the schedule-visit drawer; the header's one solid action. */
  onSchedule?: () => void;
  /** The overflow menu, built by the caller so it owns the write callbacks. */
  actionsMenu?: React.ReactNode;
  /** Whether this listing is on the signed-in broker's own shortlist. */
  favorite?: boolean;
  onToggleFavorite?: () => void;
}

const STATUS_TONE: Record<string, StatusBadgeTone> = {
  active: 'success',
  inactive: 'neutral',
  sold: 'error',
  rented: 'error',
  suspended: 'error',
  archived: 'neutral',
};

/** Every status the property model allows. */
const KNOWN_STATUSES = [
  'active', 'inactive', 'sold', 'rented', 'suspended', 'archived',
] as const;

/** How much of the identifier the code chip shows. */
const CODE_LENGTH = 8;

/**
 * Translation function type scoped to the properties namespace.
 */
type Translate = (key: string) => string;

/** The agency's own code is the solid brand chip; the generated id stays neutral. */
const AGENCY_CODE_CHIP_CLASSES = 'bg-[var(--seed-base)] text-[var(--text-inverse)]';

/**
 * Shortens the property identifier into something a broker can read aloud.
 *
 * Only shown when the listing has no code of the agency's own: then the chip
 * shows the head of the stored identifier rather than inventing a number.
 * Short enough to dictate over the phone, long enough to tell two listings
 * apart.
 */
function formatShortId(property: PropertyResponseType): string | null {
  if (!property.id) return null;
  return property.id.replace(/-/g, '').slice(0, CODE_LENGTH).toUpperCase();
}

/**
 * Builds a "/"-joined transaction label from the property's transaction types.
 * Accepts both Portuguese form labels (Venda/Aluguel) and English keys.
 */
function getTransactionLabel(types: string[] | undefined, t: Translate): string | null {
  const active = normalizeTransactionTypeKeys(types);
  const labels = active.map((key) => t(`detailView.transaction.${key}`));
  return labels.join(' / ') || null;
}

/**
 * Formats the property's address into a single display line.
 */
function formatAddress(property: PropertyResponseType): string {
  const loc = property.location;
  const streetLine =
    loc?.street && loc?.number
      ? `${loc.street}, ${loc.number}`
      : loc?.street;
  const cityLine =
    loc?.city && loc?.state
      ? `${loc.city}/${loc.state}`
      : loc?.city;
  return [streetLine, loc?.neighborhood, cityLine].filter(Boolean).join(' - ');
}

/**
 * Breadcrumb row linking back to the property list from the detail view.
 */
export const PropertyBreadcrumb: React.FC<{ property: PropertyResponseType; onBack: () => void }> = ({
  property,
  onBack,
}) => {
  const { t } = useTranslation('features/properties');
  return (
    <div className="flex items-center gap-[var(--space-2)] text-[length:var(--text-meta)] text-[var(--text-tertiary-soft)]">
      {/*
        * Negative margins on the phone so the target reaches 44px without the
        * label drifting away from the row it belongs to. A crumb is a small
        * piece of text, and on a touch screen it is also the only way back:
        * an 18px tall link is a miss waiting to happen, and now that the
        * detail screen hides the bottom navigation there is nothing else to
        * hit by accident that would help.
        */}
      <UnstyledButton
        type="button"
        onClick={onBack}
        data-testid="property-detail-back"
        className={[
          'flex items-center gap-[var(--space-1)] [font-weight:var(--font-semibold)]',
          'text-[var(--seed-base)] hover:underline underline-offset-2',
          'max-md:-my-(--space-3) max-md:min-h-11 max-md:-ml-(--space-2) max-md:pl-(--space-2) max-md:pr-(--space-1)',
        ].join(' ')}
      >
        <ChevronLeftIcon width={14} height={14} /> {t('detailView.breadcrumb')}
      </UnstyledButton>
      <Text component="span">/</Text>
      <Text component="span" className="min-w-0 truncate">
        {property.description?.headline || property.id}
      </Text>
    </div>
  );
};

/**
 * Identity block: lifecycle badges, headline and address.
 */
const PropertyIdentity: React.FC<{ property: PropertyResponseType }> = ({ property }) => {
  const { t } = useTranslation('features/properties');
  // The badge used to collapse everything into active/inactive, so a
  // sold apartment announced itself as "Ativo" -- the one case where
  // the broker most needs to be told otherwise. `sold` and `rented` are
  // written automatically when an attendance is won.
  const stored = property.classification?.status;
  const statusKey = stored && KNOWN_STATUSES.includes(stored as never) ? stored : 'active';
  const transactionLabel = getTransactionLabel(property.classification?.transaction_types, t);
  const categoryLabel = property.classification?.category
    ? getCategoryLabel(property.classification.category, t)
    : null;
  const agencyCode = propertyAgencyCode(property as unknown as Record<string, unknown>);
  const shortId = formatShortId(property);

  return (
    <div className="flex min-w-0 flex-[1_1_20rem] flex-col gap-[var(--space-1-5)]">
      <div className="flex flex-wrap items-center gap-[var(--space-2)]">
        <StatusBadge tone={STATUS_TONE[statusKey]}>{t(`detailView.status.${statusKey}`)}</StatusBadge>
        {categoryLabel && <StatusBadge tone="neutral">{categoryLabel}</StatusBadge>}
        {transactionLabel && <StatusBadge tone="brand">{transactionLabel}</StatusBadge>}
        {agencyCode ? (
          <StatusBadge className={AGENCY_CODE_CHIP_CLASSES} data-testid="property-code-chip">
            {`${t('detailView.codePrefix')} ${agencyCode}`}
          </StatusBadge>
        ) : (
          shortId && (
            <StatusBadge tone="neutral" data-testid="property-code-chip">
              {shortId}
            </StatusBadge>
          )
        )}
      </div>
      {/* Same reason as the card headings: order 1 forces --text-h1, which
          is a clamp that reaches 40px on a desktop viewport. The listing
          headline is --text-app-title (28px). */}
      <Title
        order={1}
        style={{ fontSize: 'var(--text-app-title)', fontWeight: 'var(--font-bold)' }}
        className="m-0 min-w-0 leading-[var(--leading-tight)] tracking-[var(--tracking-app-title)] text-[var(--text-primary)]"
      >
        {property.description?.headline || t('detailView.noTitle')}
      </Title>
      <Text className="m-0 min-w-0 text-[length:var(--text-body-sm)] leading-[var(--leading-normal)] text-[var(--text-secondary)]">
        {formatAddress(property) || t('detailView.noAddress')}
      </Text>
    </div>
  );
};

/**
 * Header panel showing status badges, title, address and detail actions.
 *
 * Scheduling a visit is the solid button: it is the one thing a broker does
 * from this screen that moves the listing forward. Sharing and editing sit
 * beside it as icons, and everything about the listing's lifecycle is behind
 * the overflow menu rather than competing for the same row.
 */
export const PropertyHeaderTop: React.FC<PropertyHeaderProps> = ({
  property,
  onBack: _onBack,
  onEdit,
  onShare,
  onSchedule,
  actionsMenu,
  favorite = false,
  onToggleFavorite,
}) => {
  const { t } = useTranslation('features/properties');

  return (
    <div className="flex flex-wrap items-start gap-[var(--space-4)]">
      <PropertyIdentity property={property} />
      <div className="flex min-w-0 flex-[1_1_auto] flex-wrap items-center justify-end gap-[var(--space-2)]">
        {onSchedule && (
          <Button
            variant="solid"
            onClick={onSchedule}
            data-testid="property-header-schedule"
            className="h-[38px] whitespace-nowrap px-[var(--space-4)] [font-weight:var(--font-bold)]"
          >
            {t('detailView.visits.scheduleVisit')}
          </Button>
        )}
        {/* The broker's own star: one click, state visible without opening
            anything. Not the same thing as the agency-wide "Destaque"
            badge, which the actions menu writes. */}
        {onToggleFavorite && (
          <IconAction
            label={favorite ? t('detailView.favorite.remove') : t('detailView.favorite.add')}
            onClick={onToggleFavorite}
            testId="property-favorite-toggle"
            pressed={favorite}
          >
            {favorite
              ? <StarFilledIcon width={16} height={16} />
              : <StarIcon width={16} height={16} />}
          </IconAction>
        )}
        {onShare && (
          <IconAction label={t('detailView.shareButton')} onClick={onShare} testId="property-header-share">
            <Share2Icon width={16} height={16} />
          </IconAction>
        )}
        <IconAction label={t('detailView.editButton')} onClick={onEdit} testId="property-header-edit">
          <Pencil1Icon width={16} height={16} />
        </IconAction>
        {actionsMenu}
      </div>
    </div>
  );
};

/**
 * Square icon-only action button used beside the header's solid CTA.
 *
 * @param props - Accessible label, click handler, testid and the icon.
 * @returns The rendered button.
 */
const IconAction: React.FC<{
  label: string;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
  /** Toggle buttons announce their state and carry the brand colour when on. */
  pressed?: boolean;
}> = ({ label, onClick, testId, children, pressed }) => (
  <UnstyledButton
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    aria-pressed={pressed}
    data-testid={testId}
    className={cn(
      'flex h-[38px] w-[38px] flex-none items-center justify-center rounded-(--radius-md) border border-solid border-[var(--border-medium)] bg-[var(--surface-primary)] hover:bg-[var(--surface-hover)]',
      pressed
        ? 'text-[var(--seed-base)]'
        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
    )}
  >
    {children}
  </UnstyledButton>
);

/**
 * Composite header rendering the breadcrumb above the header panel.
 */
export const PropertyHeader: React.FC<PropertyHeaderProps> = (props) => (
  <>
    <PropertyBreadcrumb property={props.property} onBack={props.onBack} />
    <PropertyHeaderTop {...props} />
  </>
);
